// src/utils/utils.js
import { getStatusLabelFromCode } from "./status";
import { useChannels } from "../hooks/useChannels";
import { salePipelineStages } from "../data/salesPipeline/constants";
import Tooltip from "@mui/material/Tooltip";
import { Chip } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import ChatIcon from "@mui/icons-material/Chat";
import SmsIcon from "@mui/icons-material/Sms";
import AppIcon from "@mui/icons-material/Apps";
import LanguageIcon from "@mui/icons-material/Language";
import AdsIcon from "@mui/icons-material/AdsClick";
import HubIcon from "@mui/icons-material/Hub";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";

/* =========================================================
   Helpers básicos
   ========================================================= */
export const formatBRL = (v) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : v ?? "—";

/** Base URL do Xpro/cockpit antigo para imagens locais (fotos de clientes em /midias/). */
const LEGACY_WEB_BASE = "https://web.xpro.me";
/** Path no servidor legado onde ficam as fotos de clientes (apenas nome de arquivo). */
const LEGACY_CLIENTES_PATH = "/midias/clientes";

/**
 * Retorna a URL absoluta para exibir a foto do cliente.
 * - URL completa (http/https): devolvida como está.
 * - Path com / (ex.: /midias/clientes/foto.jpg): prefixa LEGACY_WEB_BASE.
 * - Apenas nome de arquivo (ex.: messenger0706202210354490.jpg): monta LEGACY_WEB_BASE + /midias/clientes/ + nome.
 * @param {string|null|undefined} profilePicture - URL completa, path ou nome do arquivo
 * @returns {string} URL pronta para usar em <img src={...} />
 */
export function getClientProfilePictureUrl(profilePicture) {
  if (profilePicture == null || typeof profilePicture !== "string") return "";
  const trimmed = profilePicture.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return `${LEGACY_WEB_BASE}${trimmed}`;
  // Apenas nome de arquivo (ex.: messenger0706202210354490.jpg) → foto de cliente no Xpro antigo
  return `${LEGACY_WEB_BASE}${LEGACY_CLIENTES_PATH}/${trimmed}`;
}

/**
 * Iniciais do nome para avatar quando não há foto (ex.: "Allysson Suzano" → "AS").
 * @param {string|null|undefined} name
 * @returns {string}
 */
export function getInitials(name) {
  if (!name || typeof name !== "string") return "—";
  const trimmed = name.trim();
  if (!trimmed) return "—";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) {
    const s = parts[0];
    return s.length >= 2 ? (s[0] + s[1]).toUpperCase() : s[0].toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const stageIdByName = (needle) => {
  const n = String(needle).toLowerCase();
  const found =
    salePipelineStages.find((s) => String(s.name).toLowerCase().startsWith(n)) ||
    salePipelineStages.find((s) => String(s.name).toLowerCase().includes(n));
  return found?.id ?? null;
};

const STAGE = {
  CART_PENDING: stageIdByName("Carrinho pendente"),
  CART_ABANDONED: stageIdByName("Carrinho abandonado"),
  CHECKOUT_STARTED: stageIdByName("Checkout iniciado"),
  AWAITING_PAYMENT: stageIdByName("Aguardando pagamento"),
  PAYMENT_PROCESSING: stageIdByName("Pagamento em processamento"),
  PAYMENT_DENIED: stageIdByName("Pagamento recusado"),
  ANTIFRAUD_REVIEW: stageIdByName("Antifraude"),
  ANTIFRAUD_DENIED: stageIdByName("reprovado"),
  APPROVED: stageIdByName("Pedido aprovado"),
  PICKING: stageIdByName("Em separação"),
  INVOICED: stageIdByName("Faturado"),
  PACKING: stageIdByName("Em embalagem"),
  SHIPPED: stageIdByName("Enviado"),
  IN_TRANSIT: stageIdByName("Em transporte"),
  OUT_FOR_DELIVERY: stageIdByName("Saiu para entrega"),
  DELIVERED: stageIdByName("Entregue"),
  DELIVERY_ISSUE: stageIdByName("Problema na entrega"),
  RETURN: stageIdByName("Devolução"),
  REFUNDED: stageIdByName("Reembolsado"),
  CANCELED: stageIdByName("Cancelado"),
  CHARGEBACK: stageIdByName("Chargeback"),
};

/* =========================================================
   Catálogos simples para montar exemplos realistas
   ========================================================= */
const COMPANIES = [
  { name: "TransLog Transportes LTDA", doc: "14.298.001/0001-52" },
  { name: "Rodoviário Santa Luzia ME", doc: "05.812.733/0001-17" },
  { name: "ConstruSul Engenharia Ltda", doc: "27.119.402/0001-90" },
  { name: "AgroVale Coop. Agropecuária", doc: "01.336.845/0001-03" },
  { name: "Metalúrgica Aurora Ind. e Com.", doc: "33.908.712/0001-61" },
  { name: "Mineração Vale Verde S.A.", doc: "09.550.100/0001-44" },
  { name: "Distribuidora Alfa Peças LTDA", doc: "21.415.300/0001-08" },
  { name: "EnergiX Geradores do Brasil", doc: "18.771.201/0001-70" },
  { name: "Cooperativa AgroVale", doc: "62.101.333/0001-28" },
  { name: "Cliente (Pessoa Física)", doc: "106.222.119-00" },
];

const ITEMS = [
  { sku: "SCN-BMB-4PGR", name: "Bomba hidráulica — Série 4/PGR (1998—2024)", brand: "Scania", price: 6919.94 },
  { sku: "SKF-RLM-80", name: "Rolamento de roda 80mm — SKF", brand: "SKF", price: 495.50 },
  { sku: "KIT-COR-TR", name: "Kit correia dentada — Bosch", brand: "Bosch", price: 459.00 },
  { sku: "ABS-SENSOR-R", name: "Sensor ABS roda traseira — Scania", brand: "Scania", price: 159.75 },
  { sku: "JPF-DIANT", name: "Jogo de pastilhas de freio (Dianteiras) — TRW", brand: "TRW", price: 329.50 },
  { sku: "AMT-DI-HD", name: "Amortecedor dianteiro HeavyDuty — Série 4/PGR", brand: "HeavyDuty", price: 3299.90 },
];

const ADDRESS = [
  { line: "Av. Central, 417 — Jardins, Curitiba/PR", zip: "80060-000", city: "Curitiba", state: "PR" },
  { line: "Av. dos Jasmins, 820 — Jardins, Florianópolis/SC", zip: "88061-701", city: "Florianópolis", state: "SC" },
  { line: "Av. Projetada, 533 — Jardins, São José/SC", zip: "88100-000", city: "São José", state: "SC" },
  { line: "Av. Brasil, 355 — Industrial, Campinas/SP", zip: "13041-212", city: "Campinas", state: "SP" },
];

const CHANNELS = [
  { type: "Site", label: "Site" },
  { type: "App", label: "App" },
  { type: "WhatsApp", label: "WhatsApp" },
  { type: "Telefone", label: "Telefone" },
];

const makeItem = (base, qty) => ({
  sku: base.sku,
  name: base.name,
  brand: base.brand,
  qty,
  unitPrice: base.price,
  subtotal: base.price * qty,
});

const sumItems = (arr) => arr.reduce((acc, it) => acc + it.subtotal, 0);

/** Monta um "pedido interno" completo */
const makeOrder = ({ id, company, channel, items, address, payment, antifraud, shipping, createdAt }) => {
  const subtotal = sumItems(items);
  const freight = shipping.freight ?? 0;
  const discount = payment.discount ?? 0;
  const total = subtotal + freight - discount;

  return {
    id, // "o2025-1001"
    orderCode: `COD-${id.replace(/\D/g, "").slice(0, 4)}-${id.replace(/\D/g, "").slice(-4)}`,
    customer: { name: company.name, doc: company.doc, type: company.name.includes("Pessoa Física") ? "person" : "company" },
    channel,
    items,
    amounts: { subtotal, freight, discount, total },
    payment,
    antifraud,
    shipping,
    address,
    createdAt,
  };
};

/** Converte para o shape que os cards esperam (achatado e sem marca do antifraude) */
const toCardVM = (order) => ({
  id: order.id,
  orderCode: order.orderCode,
  companyName: order.customer.name,
  channelType: order.channel.type,
  createdAtLabel: order.createdAt,

  // itens — para o card usamos name/qty e price (unitário) no modal
  items: order.items.map((it) => ({ qty: it.qty, name: it.name, price: it.unitPrice })),

  // totais “achatados”
  subtotal: order.amounts.subtotal,
  shippingCost: order.amounts.freight,
  total: order.amounts.total,

  // pagamento / antifraude (genérico)
  payment: {
    methodLabel: order.payment.method,
    statusLabel: order.payment.status,
    transactionId: order.payment.gatewayId || null,
    lastUpdate: order.payment.lastUpdate || null,
  },
  antifraud: {
    statusLabel: order.antifraud.status || "em análise",
    score: order.antifraud.score ?? null, // não exibimos marca
  },

  // envio
  shipping: {
    methodLabel: order.shipping.method,
    statusLabel: order.shipping.status,
    carrier: order.shipping.carrier,
    tracking: order.shipping.tracking || null,
    eta: order.shipping.eta || null,
  },

  // endereço (linha curta)
  address: {
    street: order.address.line,
    city: order.address.city,
    uf: order.address.state,
  },
});

/* =========================================================
   Gera exemplos fictícios para o pipeline (shape final dos cards)
   ========================================================= */
export const generateExampleCases = () => {
  const byId = Object.fromEntries(salePipelineStages.map((s) => [s.id, []]));

  // Carrinho pendente
  byId[STAGE.CART_PENDING].push(
    toCardVM(
      makeOrder({
        id: "o2025-1001",
        company: COMPANIES[0],
        channel: CHANNELS[3],
        items: [makeItem(ITEMS[1], 2), makeItem(ITEMS[4], 1)],
        address: ADDRESS[3],
        payment: { method: "—", status: "sem pagamento", discount: 0, gatewayId: null, lastUpdate: "27/08/2025, 20:47" },
        antifraud: { status: "não iniciado", score: null },
        shipping: { method: "Definir", carrier: "—", service: "—", freight: 38.9, eta: "—", status: "pendente", tracking: null },
        createdAt: "27/08/2025, 20:47",
      })
    ),
    toCardVM(
      makeOrder({
        id: "o2025-1002",
        company: COMPANIES[4],
        channel: CHANNELS[0],
        items: [makeItem(ITEMS[4], 3)],
        address: ADDRESS[0],
        payment: { method: "—", status: "sem pagamento", discount: 0, gatewayId: null, lastUpdate: "27/08/2025, 20:41" },
        antifraud: { status: "não iniciado", score: null },
        shipping: { method: "Definir", carrier: "—", service: "—", freight: 0, eta: "—", status: "pendente", tracking: null },
        createdAt: "27/08/2025, 20:41",
      })
    )
  );

  // Carrinho abandonado
  byId[STAGE.CART_ABANDONED].push(
    toCardVM(
      makeOrder({
        id: "o2025-1101",
        company: COMPANIES[1],
        channel: CHANNELS[2],
        items: [makeItem(ITEMS[5], 3), makeItem(ITEMS[3], 2)],
        address: ADDRESS[1],
        payment: { method: "—", status: "sem pagamento", discount: 0, gatewayId: null, lastUpdate: "há 2 h" },
        antifraud: { status: "não iniciado", score: null },
        shipping: { method: "Definir", carrier: "—", service: "—", freight: 0, eta: "—", status: "pendente", tracking: null },
        createdAt: "há 2 h",
      })
    ),
    toCardVM(
      makeOrder({
        id: "o2025-1102",
        company: COMPANIES[5],
        channel: CHANNELS[0],
        items: [makeItem(ITEMS[0], 2)],
        address: ADDRESS[1],
        payment: { method: "—", status: "sem pagamento", discount: 0, gatewayId: null, lastUpdate: "há 3 h" },
        antifraud: { status: "não iniciado", score: null },
        shipping: { method: "Definir", carrier: "—", service: "—", freight: 0, eta: "—", status: "pendente", tracking: null },
        createdAt: "há 3 h",
      })
    )
  );

  // Checkout iniciado
  byId[STAGE.CHECKOUT_STARTED].push(
    toCardVM(
      makeOrder({
        id: "o2025-1201",
        company: COMPANIES[2],
        channel: CHANNELS[2],
        items: [makeItem(ITEMS[2], 2)],
        address: ADDRESS[2],
        payment: { method: "Boleto", status: "iniciado", discount: 0, gatewayId: "BOL-22C5", lastUpdate: "27/08/2025, 20:41" },
        antifraud: { status: "não aplicável (boleto)", score: null },
        shipping: { method: "Transportadora", carrier: "Jadlog", service: "Econômico", freight: 64.9, eta: "3–6 dias úteis", status: "aguardando pagamento", tracking: null },
        createdAt: "27/08/2025, 20:41",
      })
    ),
    toCardVM(
      makeOrder({
        id: "o2025-1202",
        company: COMPANIES[3],
        channel: CHANNELS[1],
        items: [makeItem(ITEMS[1], 1), makeItem(ITEMS[4], 2)],
        address: ADDRESS[0],
        payment: { method: "Cartão de crédito", status: "iniciado", discount: 0, gatewayId: "CRT-9A03", lastUpdate: "27/08/2025, 20:47" },
        antifraud: { status: "em análise", score: 66 },
        shipping: { method: "Transportadora", carrier: "Correios", service: "SEDEX", freight: 64.9, eta: "2–4 dias úteis", status: "aguardando liberação", tracking: null },
        createdAt: "27/08/2025, 20:47",
      })
    )
  );

  // Aguardando pagamento
  byId[STAGE.AWAITING_PAYMENT].push(
    toCardVM(
      makeOrder({
        id: "o2025-1301",
        company: COMPANIES[7],
        channel: CHANNELS[0],
        items: [makeItem(ITEMS[3], 1), makeItem(ITEMS[2], 1)],
        address: ADDRESS[3],
        payment: { method: "PIX", status: "pendente", discount: 0, gatewayId: "PIX-7BC1", lastUpdate: "27/08/2025, 20:45" },
        antifraud: { status: "não aplicável (pix)", score: null },
        shipping: { method: "Transportadora", carrier: "Jadlog", service: "Econômico", freight: 29.9, eta: "2–5 dias úteis após confirmação", status: "aguardando pagamento", tracking: null },
        createdAt: "27/08/2025, 20:45",
      })
    )
  );

  // Em transporte
  byId[STAGE.IN_TRANSIT].push(
    toCardVM(
      makeOrder({
        id: "o2025-1501",
        company: COMPANIES[8],
        channel: CHANNELS[0],
        items: [makeItem(ITEMS[4], 10)],
        address: ADDRESS[2],
        payment: { method: "Boleto", status: "compensado", discount: 0, gatewayId: "BOL-8801", lastUpdate: "26/08/2025, 15:22" },
        antifraud: { status: "não aplicável (boleto)", score: null },
        shipping: { method: "Transportadora", carrier: "Correios", service: "PAC", freight: 49.9, eta: "4–8 dias úteis", status: "em transporte", tracking: "LB123456789BR" },
        createdAt: "26/08/2025, 11:05",
      })
    )
  );

  // Entregue
  byId[STAGE.DELIVERED].push(
    toCardVM(
      makeOrder({
        id: "o2025-1601",
        company: COMPANIES[2],
        channel: CHANNELS[2],
        items: [makeItem(ITEMS[2], 1), makeItem(ITEMS[3], 2)],
        address: ADDRESS[2],
        payment: { method: "PIX", status: "aprovado", discount: 0, gatewayId: "PIX-7731", lastUpdate: "21/08/2025, 09:11" },
        antifraud: { status: "não aplicável (pix)", score: null },
        shipping: { method: "Transportadora", carrier: "Jadlog", service: "Econômico", freight: 34.9, eta: "3–6 dias úteis", status: "entregue", tracking: "JD8830BR5521" },
        createdAt: "20/08/2025, 14:30",
      })
    )
  );

  return byId;
};

/* =========================================================
   View-model (mantida para usos externos)
   ========================================================= */
export const buildOrderDetailViewModel = (card) => {
  if (!card) return null;
  const header = {
    title: `Pedido #${card.orderCode}`,
    subtitle: card.companyName,
    pills: [
      { label: "Subtotal", value: formatBRL(card.subtotal) },
      { label: "Frete", value: formatBRL(card.shippingCost) },
      { label: "Total", value: formatBRL(card.total), color: "primary" },
    ],
  };
  const sections = [
    {
      icon: "cart",
      title: "Itens do pedido",
      type: "table",
      headers: ["Qtd", "Item", "Vlr unit.", "Subtotal"],
      rows: card.items.map((it) => [
        String(it.qty),
        it.name,
        formatBRL(it.price),
        formatBRL(it.price * it.qty),
      ]),
    },
  ];
  return { header, sections };
};

/* =========================================================
   Canais
   ========================================================= */
export const GetChannelTypeById = (channelId) => {
  const { data: channelsData } = useChannels();
  if (!channelsData?.channels || !Array.isArray(channelsData.channels)) return null;
  const channel = channelsData.channels.find((ch) => parseInt(ch.id) === parseInt(channelId));
  return channel ? channel.channelType : null;
};

export const GetChannelById = (channelId) => {
  const { data: channelsData } = useChannels();
  if (!channelsData?.channels || !Array.isArray(channelsData.channels)) return null;
  const channel = channelsData.channels.find((ch) => parseInt(ch.id) === parseInt(channelId));
  return channel || null;
};

/* =========================================================
   Ícones de canal
   ========================================================= */
export const getChannelIcon = (channel, type) => {
  const tooltipText = channel || "Origem desconhecida";
  switch (type) {
    case "WhatsApp":
      return (<Tooltip title={tooltipText}><WhatsAppIcon color="success" /></Tooltip>);
    case "Email":
      return (<Tooltip title={tooltipText}><EmailIcon color="primary" /></Tooltip>);
    case "Phone":
    case "Telefone":
      return (<Tooltip title={tooltipText}><PhoneIcon color="action" /></Tooltip>);
    case "Messenger":
      {
        const name = String(channel || "").toLowerCase();
        if (name.includes("instagram")) {
          return (
            <Tooltip title={tooltipText}>
              <InstagramIcon sx={{ color: "#C13584" }} />
            </Tooltip>
          );
        }
        if (name.includes("facebook") || name.includes("messenger") || name.includes("fb")) {
          return (
            <Tooltip title={tooltipText}>
              <FacebookIcon sx={{ color: "#1877F2" }} />
            </Tooltip>
          );
        }
        return (
          <Tooltip title={tooltipText}>
            <ChatIcon color="secondary" />
          </Tooltip>
        );
      }
    case "SMS":
      return (<Tooltip title={tooltipText}><SmsIcon color="warning" /></Tooltip>);
    case "App":
      return (<Tooltip title={tooltipText}><AppIcon color="info" /></Tooltip>);
    case "Wordpress":
    case "Site":
      return (<Tooltip title={tooltipText}><LanguageIcon color="primary" /></Tooltip>);
    case "Ads":
      return (<Tooltip title={tooltipText}><AdsIcon color="secondary" /></Tooltip>);
    default:
      return (<Tooltip title={tooltipText}><HubIcon color="disabled" /></Tooltip>);
  }
};

/* =========================================================
   Chips de status e atribuição (mantidos)
   ========================================================= */
export const getStatusChip = (status, theme) => {
  const label = getStatusLabelFromCode(status);
  const chipStyles = {
    Pendente: { bg: theme.palette.status.pending, color: theme.palette.text.primary },
    "Em Atendimento": { bg: theme.palette.status.inProgress, color: theme.palette.text.primary },
    "Contato Futuro": { bg: theme.palette.status.future, color: theme.palette.text.primary },
    Concluída: { bg: theme.palette.status.success, color: theme.palette.common.white },
    Perdida: { bg: theme.palette.status.unsuccessful, color: theme.palette.common.white },
    Cancelada: { bg: theme.palette.status.canceled, color: theme.palette.common.white },
  };
  const styles = chipStyles[label];
  if (!styles) return null;
  return (
    <Tooltip title={label}>
      <Chip
        label={label}
        size="small"
        sx={{ backgroundColor: styles.bg, color: styles.color, fontSize: "0.75rem", height: "24px" }}
      />
    </Tooltip>
  );
};

export const getAssignedChip = (userData, assignedTo, theme) => {
  if (assignedTo === userData?.nome) {
    return (
      <Tooltip title="Minha Solicitação">
        <Chip
          label="Minha Solicitação"
          size="small"
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            fontWeight: "bold",
            fontSize: "0.75rem",
            height: "24px",
            maxWidth: "200px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        />
      </Tooltip>
    );
  }
  if (assignedTo === null) {
    return (
      <Tooltip title="Fila">
        <Chip
          label="Fila"
          size="small"
          sx={{
            backgroundColor: theme.palette.warning.main,
            color: theme.palette.common.white,
            fontWeight: "bold",
            fontSize: "0.75rem",
            height: "24px",
            maxWidth: "120px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`Atribuído a ${assignedTo}`}>
      <Chip
        label={assignedTo}
        size="small"
        sx={{
          color: theme.palette.text.primary,
          fontWeight: "bold",
          fontSize: "0.75rem",
          height: "24px",
          maxWidth: "300px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      />
    </Tooltip>
  );
};
